import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiQuery } from '../useApiQuery';
import { useApiMutation } from '../useApiMutation';
import { contactKeys, taskKeys } from '@/lib/queryKeys';
import { apiGet, apiPost, apiPatch, apiDelete, operationsClient } from '@/lib/api';
import type { ContactTask } from '@/types/contact';
import type { TaskType } from '@/types/task';

/** The scheduling fields the task form writes (CreateTaskDto/UpdateTaskDto). */
type TaskWriteFields = {
  type?: TaskType;
  durationMinutes?: number | null;
  remindMinutesBefore?: number | null;
  technicianId?: string | null;
  locationId?: string | null;
};

export function useContactTasks(contactId: string, options?: { limit?: number }) {
  return useApiQuery<{ data: ContactTask[]; total: number }>({
    queryKey: [...contactKeys.detail(contactId), 'tasks', options?.limit ?? null],
    queryFn: () =>
      apiGet(operationsClient, `/contacts/${contactId}/tasks`, {
        params: options?.limit ? { limit: options.limit } : undefined,
      }),
    enabled: Boolean(contactId),
    showErrorToast: false,
  });
}

export function useCreateContactTask(contactId: string) {
  const qc = useQueryClient();
  return useApiMutation<
    ContactTask,
    {
      title: string;
      description?: string;
      dueDate?: string;
      assignedTo?: string | null;
    } & TaskWriteFields
  >({
    mutationFn: (
      dto: {
        title: string;
        description?: string;
        dueDate?: string;
        assignedTo?: string | null;
      } & TaskWriteFields,
    ) => apiPost<ContactTask>(operationsClient, `/contacts/${contactId}/tasks`, dto),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...contactKeys.detail(contactId), 'tasks'],
      });
      void qc.invalidateQueries({ queryKey: taskKeys.all() });
      toast.success('Task created');
    },
  });
}

export function useUpdateContactTask(contactId: string) {
  const qc = useQueryClient();
  return useApiMutation<
    ContactTask,
    {
      taskId: string;
      title?: string;
      description?: string | null;
      status?: string;
      dueDate?: string | null;
      assignedTo?: string | null;
    } & TaskWriteFields
  >({
    mutationFn: ({
      taskId,
      ...dto
    }: {
      taskId: string;
      title?: string;
      description?: string | null;
      status?: string;
      dueDate?: string | null;
      assignedTo?: string | null;
    } & TaskWriteFields) =>
      apiPatch<ContactTask>(operationsClient, `/contact-tasks/${taskId}`, dto),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...contactKeys.detail(contactId), 'tasks'],
      });
      void qc.invalidateQueries({ queryKey: taskKeys.all() });
      toast.success('Task updated');
    },
  });
}

export function useDeleteContactTask(contactId: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (taskId: string) => apiDelete(operationsClient, `/contact-tasks/${taskId}`),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...contactKeys.detail(contactId), 'tasks'],
      });
      void qc.invalidateQueries({ queryKey: taskKeys.all() });
      toast.success('Task deleted');
    },
  });
}
